import React, { useMemo } from "react";
import {
  type GameState,
  type PhaseTimerStatus,
  type PlayerKey,
  type WaterManagement,
} from "../vowGameLogic";
import { PANEL_CLASS } from "./uiConstants";
import RoleCard from "./controlPanel/RoleCard";
import SimulationStep from "./controlPanel/SimulationStep";
import { formatCountdown } from "../utils/time";

type ControlPanelProps = {
  gameState: GameState;
  timerStatus?: PhaseTimerStatus | null;
  playerId: number;
  selfRole: PlayerKey | null;
  roleLocked: boolean;
  onWaterManagementChange: (value: WaterManagement) => void;
  onSelectRole: () => void;
  selfPlayerName?: string;
};

const ControlPanel: React.FC<ControlPanelProps> = ({
  gameState,
  timerStatus,
  playerId,
  selfRole,
  roleLocked,
  onWaterManagementChange,
  onSelectRole,
  selfPlayerName,
}) => {
  const actionTimerCountdown = useMemo(() => {
    if (
      !timerStatus ||
      timerStatus.phase !== "actions" ||
      timerStatus.hasExpired
    ) {
      return null;
    }
    return formatCountdown(timerStatus.remainingMs);
  }, [timerStatus]);
  const actionTimerExpired =
    timerStatus?.phase === "actions" && timerStatus.hasExpired;
  const simulationTimerExpired =
    timerStatus?.phase === "simulation" && timerStatus.hasExpired;
  const simulationTimerCountdown = useMemo(() => {
    if (
      !timerStatus ||
      timerStatus.phase !== "simulation" ||
      timerStatus.hasExpired
    ) {
      return null;
    }
    return formatCountdown(timerStatus.remainingMs);
  }, [timerStatus]);
  const playerVote = gameState.wmVotes[playerId] ?? null;

  return (
    <section className={`${PANEL_CLASS} p-6`}>
      <h2 className="text-2xl font-bold text-slate-900">Control Panel</h2>

      <div className="mt-4 space-y-6">
        <RoleCard
          playerId={playerId}
          selfPlayerName={selfPlayerName}
          selfRole={selfRole}
          roleLocked={roleLocked}
          onSelectRole={onSelectRole}
        />

        <div className="rounded-lg border border-black bg-[#faf3e2] px-4 py-3 text-sm text-slate-800">
          {gameState.gameEnded ? (
            <p>The host has ended the game. Review the final board or reset to play again.</p>
          ) : gameState.reachedDefaultEnding ? (
            <p>The base game is complete. Waiting for the host to extend or end the session.</p>
          ) : gameState.phase === "actions" ? (
            actionTimerExpired ? (
              <p>The planning timer has ended. Wait for the host to move the game forward.</p>
            ) : (
              <p>
                Use the game board to choose tiles for your action. The board shows your current action
                and provides a button to submit once you are satisfied with the selection.
                {actionTimerCountdown && (
                  <>
                    {" "}
                    <span className="font-semibold">Timer remaining: {actionTimerCountdown}</span>.
                  </>
                )}
              </p>
            )
          ) : simulationTimerExpired ? (
            <p>The voting timer has ended. The host will trigger the simulation shortly.</p>
          ) : (
            <p>
              Waiting for the next action phase. Review map views or the event log while the host
              advances the game.
              {simulationTimerCountdown && (
                <>
                  {" "}
                  <span className="font-semibold">
                    Voting timer remaining: {simulationTimerCountdown}
                  </span>.
                </>
              )}
            </p>
          )}
        </div>

        <SimulationStep
          phase={gameState.phase}
          timerExpired={simulationTimerExpired}
          playerVote={playerVote}
          hostSelection={gameState.wmChoice}
          onWaterManagementChange={onWaterManagementChange}
        />
      </div>
    </section>
  );
};

export default ControlPanel;
